(() => {
  'use strict';
  document.documentElement.classList.remove('no-js');
  const initialized = new WeakSet();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => motion.matches ? 'auto' : 'smooth';
  const each = (root, selector, init) => root.querySelectorAll(selector).forEach(element => {
    if (initialized.has(element)) return;
    initialized.add(element);
    init(element);
  });

  function initialize(root = document) {
    each(root, '[data-menu-toggle]', button => {
      const panel = document.getElementById(button.getAttribute('aria-controls'));
      if (!panel) return;
      const setOpen = (open, restoreFocus = false) => {
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        panel.hidden = !open;
        panel.classList.toggle('is-open', open);
        document.body.classList.toggle('menu-open', open);
        if (open) panel.querySelector('a, summary')?.focus();
        else if (restoreFocus) button.focus();
      };
      button.addEventListener('click', () => setOpen(button.getAttribute('aria-expanded') !== 'true'));
      panel.addEventListener('click', event => { if (event.target.closest('a')) setOpen(false); });
      const onKey = event => {
        if (panel.hidden) return;
        if (event.key === 'Escape') { setOpen(false, true); return; }
        if (event.key !== 'Tab') return;
        const focusable = [button, ...panel.querySelectorAll('a, button, summary, input, select')].filter(el => el.getClientRects().length);
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', onKey);
      const desktop = matchMedia('(min-width: 1101px)');
      const resize = () => { if (desktop.matches) setOpen(false); };
      desktop.addEventListener('change', resize);
      button._cleanup = () => { document.removeEventListener('keydown', onKey); desktop.removeEventListener('change', resize); setOpen(false); };
    });

    each(root, '[data-sort-by]', select => select.addEventListener('change', () => {
      const url = new URL(location.href);
      url.searchParams.set('sort_by', select.value);
      url.searchParams.delete('page');
      location.assign(url.toString());
    }));

    each(root, '[data-sticky-add]', button => button.addEventListener('click', () => {
      const form = document.querySelector('[data-product-form]');
      const submit = form?.querySelector('[data-product-submit]');
      if (form && submit && !submit.disabled) form.requestSubmit(submit);
    }));

    each(root, '[data-gallery]', gallery => {
      const slides = [...gallery.querySelectorAll('[data-slide]')];
      const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
      const counter = gallery.querySelector('[data-gallery-index]');
      if (!slides.length) return;
      let current = 0;
      const show = index => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          slide.classList.toggle('is-active', i === current);
          slide.setAttribute('aria-hidden', String(i !== current));
          if (i !== current) slide.querySelectorAll('video').forEach(video => video.pause());
        });
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === current);
          thumb.setAttribute('aria-pressed', String(i === current));
        });
        if (counter) counter.textContent = String(current + 1);
      };
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
      gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => show(current - 1));
      gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => show(current + 1));
      gallery.addEventListener('variant:media', event => {
        const index = slides.findIndex(slide => slide.dataset.mediaId === event.detail);
        if (index >= 0) show(index);
      });
      show(0);
    });

    each(root, '[data-variant-select]', select => {
      const scope = select.closest('.shopify-section') || document;
      const update = (updateUrl = true) => {
        const option = select.selectedOptions[0];
        if (!option) return;
        const available = option.dataset.available === 'true';
        scope.querySelectorAll('[data-product-price], [data-sticky-price]').forEach(el => el.textContent = option.dataset.price || '');
        scope.querySelectorAll('[data-product-submit], [data-sticky-add]').forEach(button => {
          button.disabled = !available;
          button.textContent = available ? button.dataset.addLabel || 'Add to cart' : 'Sold out';
        });
        const compare = scope.querySelector('[data-product-compare-price]');
        if (compare) {
          compare.textContent = option.dataset.comparePrice || '';
          compare.hidden = !option.dataset.comparePrice;
        }
        if (option.dataset.mediaId) scope.querySelector('[data-gallery]')?.dispatchEvent(new CustomEvent('variant:media', { detail: option.dataset.mediaId }));
        if (updateUrl) {
          const url = new URL(location.href);
          url.searchParams.set('variant', select.value);
          history.replaceState({}, '', url.toString());
        }
      };
      select.addEventListener('change', () => update());
      update(false);
    });

    each(root, '[data-reveal-toggle]', toggle => {
      const card = toggle.closest('.reveal-card');
      const panel = card?.querySelector('[data-reveal-panel]');
      if (!panel) return;
      panel.hidden = true;
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        card.classList.toggle('is-open', open);
        panel.hidden = !open;
      });
    });

    each(root, '[data-reveal-scope]', scope => {
      const tabs = [...scope.querySelectorAll('[data-tab-filter]')];
      tabs.forEach(tab => tab.addEventListener('click', () => {
        tabs.forEach(other => { other.classList.toggle('is-active', other === tab); other.setAttribute('aria-pressed', String(other === tab)); });
        scope.querySelectorAll('[data-tab-item]').forEach(item => item.hidden = ![tab.dataset.tabFilter, 'all'].includes(item.dataset.tabItem));
        scope.querySelector('[data-tab-panel]')?.scrollTo({ left: 0, behavior: behavior() });
      }));
      tabs[0]?.click();
    });

    each(root, '[data-rail]', rail => {
      const scope = rail.closest('section');
      const previous = scope?.querySelector('[data-rail-prev]'), next = scope?.querySelector('[data-rail-next]');
      const step = () => (rail.firstElementChild?.getBoundingClientRect().width || rail.clientWidth * .8) + (parseFloat(getComputedStyle(rail).columnGap) || 20);
      const sync = () => {
        if (previous) previous.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
      };
      previous?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: behavior() }));
      next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: behavior() }));
      rail.addEventListener('scroll', sync, { passive: true });
      const observer = new ResizeObserver(sync); observer.observe(rail);
      rail._cleanup = () => observer.disconnect();
      sync();
    });

    each(root, '.header-shell--overlay', shell => {
      const sync = () => shell.classList.toggle('is-stuck', scrollY > 40);
      window.addEventListener('scroll', sync, { passive: true }); sync();
      shell._cleanup = () => window.removeEventListener('scroll', sync);
    });

    each(root, '[data-expand]', wrap => {
      const toggle = wrap.querySelector('[data-expand-toggle]'), body = wrap.querySelector('[data-expand-body]');
      if (!toggle || !body) return;
      const sync = () => { if (!wrap.classList.contains('is-open')) toggle.hidden = body.scrollHeight <= body.clientHeight + 2; };
      toggle.addEventListener('click', () => {
        const open = wrap.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(open));
        wrap.querySelector('[data-expand-more]').hidden = open;
        wrap.querySelector('[data-expand-less]').hidden = !open;
      });
      const observer = new ResizeObserver(sync); observer.observe(body);
      wrap._cleanup = () => observer.disconnect(); sync();
    });

    each(root, '[data-hero-video]', video => {
      const toggle = video.closest('.hero')?.querySelector('[data-video-toggle]');
      const mobile = matchMedia('(max-width: 800px)');
      const connection = navigator.connection;
      let attached = false, pausedByUser = false;
      const allowed = () => !motion.matches && !connection?.saveData && !(mobile.matches && video.dataset.hideMobile === 'true');
      const sync = () => {
        if (!allowed()) { video.pause(); video.hidden = true; if (toggle) toggle.hidden = true; return; }
        if (toggle) toggle.hidden = false;
        if (!attached) {
          video.querySelectorAll('source[data-src]').forEach(source => source.src = source.dataset.src);
          video.load(); attached = true;
        }
        if (!pausedByUser && !document.hidden) video.play().catch(() => { video.hidden = true; });
        else video.pause();
      };
      video.addEventListener('playing', () => { video.hidden = false; if (toggle) toggle.textContent = 'Pause background video'; });
      video.addEventListener('pause', () => { if (toggle) toggle.textContent = 'Play background video'; });
      video.addEventListener('error', () => { video.hidden = true; if (toggle) toggle.hidden = true; });
      toggle?.addEventListener('click', () => { pausedByUser = !pausedByUser; sync(); });
      motion.addEventListener('change', sync); mobile.addEventListener('change', sync); document.addEventListener('visibilitychange', sync);
      video._cleanup = () => { motion.removeEventListener('change', sync); mobile.removeEventListener('change', sync); document.removeEventListener('visibilitychange', sync); video.pause(); };
      sync();
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('*').forEach(element => element._cleanup?.()));
})();
